import { t } from '@lingui/core/macro'
import { useSaveDocumentMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useSaveDocumentMetadataUserEdit'
import { useSaveDocumentPageMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useSaveDocumentPageMetadataUserEdit'
import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditEntry,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { FamilyDocumentMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import {
  mergeDocumentMetadataValues,
  mergePageMetadataValues,
} from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { useInvalidateDocumentMetadataUserEditLoads } from 'features/knowledgeDocumentViewer/utils/invalidateDocumentMetadataUserEditLoads'
import { useInvalidateDocumentPageMetadataUserEditLoads } from 'features/knowledgeDocumentViewer/utils/invalidateDocumentPageMetadataUserEditLoads'
import {
  allowsCrossSurfaceMetadataCascade,
  buildPageValuesCascadedFromDocumentEdit,
} from 'features/knowledgeDocumentViewer/utils/sharedMetadataFieldBridge'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useState } from 'react'

export type UseDocumentMetadataUserEditSaveActionsArgs = {
  bqDocumentId: string | undefined
  systemDocument: SystemDocumentEntry | undefined
  documentUserEdit: DocumentMetadataUserEditEntry | undefined
  mutateDocumentUserEdit: () => Promise<unknown>
  pageNum?: number
  pageEntry?: SystemDocumentPageEntry
  pageUserEdit?: DocumentPageMetadataUserEditEntry
  mutatePageUserEdit: () => Promise<unknown>
}

export type UseDocumentMetadataUserEditSaveActionsResult = {
  isSaving: boolean
  saveDocumentMetadata: (
    userValues: DocumentMetadataUserEditValues
  ) => Promise<void>
  saveDocumentMetadataFamily: (
    userValues: DocumentMetadataUserEditValues,
    members: readonly FamilyDocumentMetadataSaveMember[]
  ) => Promise<void>
}

export function useDocumentMetadataUserEditSaveActions({
  bqDocumentId,
  systemDocument,
  documentUserEdit,
  mutateDocumentUserEdit,
  pageNum,
  pageEntry,
  pageUserEdit,
  mutatePageUserEdit,
}: UseDocumentMetadataUserEditSaveActionsArgs): UseDocumentMetadataUserEditSaveActionsResult {
  const { saveDocumentMetadataUserEdit, saveDocumentMetadataUserEditFamily } =
    useSaveDocumentMetadataUserEdit()
  const {
    savePageMetadataUserEdit,
    saveCascadedPageMetadataFromDocumentFamily,
  } = useSaveDocumentPageMetadataUserEdit()
  const invalidateDocumentMetadataUserEditLoads =
    useInvalidateDocumentMetadataUserEditLoads()
  const invalidateDocumentPageMetadataUserEditLoads =
    useInvalidateDocumentPageMetadataUserEditLoads()
  const { errorNotification } = useGeckoNotifications()
  const [isSaving, setIsSaving] = useState(false)

  const cascadeAlignedFieldsToPage = useCallback(
    async (documentNext: DocumentMetadataUserEditValues) => {
      if (!bqDocumentId || pageNum === undefined || pageEntry === undefined) {
        return
      }
      if (
        !allowsCrossSurfaceMetadataCascade(
          systemDocument?.entry.data.total_pages
        )
      ) {
        return
      }
      const documentBaseline = mergeDocumentMetadataValues(
        systemDocument?.entry.data,
        documentUserEdit?.entry.data.values
      )
      const pageBaseline = mergePageMetadataValues(
        pageEntry.entry.data,
        pageUserEdit?.entry.data.values
      )
      const cascaded = buildPageValuesCascadedFromDocumentEdit({
        documentBaseline,
        documentNext,
        pageBaseline,
      })
      if (cascaded === undefined) return
      await savePageMetadataUserEdit({
        bqDocumentId,
        pageNum,
        systemPage: pageEntry,
        priorEdit: pageUserEdit,
        userValues: cascaded,
      })
      await mutatePageUserEdit()
      await invalidateDocumentPageMetadataUserEditLoads()
    },
    [
      bqDocumentId,
      documentUserEdit?.entry.data.values,
      invalidateDocumentPageMetadataUserEditLoads,
      mutatePageUserEdit,
      pageEntry,
      pageNum,
      pageUserEdit,
      savePageMetadataUserEdit,
      systemDocument?.entry.data,
    ]
  )

  const saveDocumentMetadata = useCallback(
    async (userValues: DocumentMetadataUserEditValues) => {
      if (!bqDocumentId) {
        errorNotification(
          t`Cannot save document metadata without a document id`
        )
        throw new Error('Cannot save document metadata without a document id')
      }
      setIsSaving(true)
      try {
        await saveDocumentMetadataUserEdit({
          bqDocumentId,
          systemDocument,
          priorEdit: documentUserEdit,
          userValues,
        })
        await cascadeAlignedFieldsToPage(userValues)
        await mutateDocumentUserEdit()
        await invalidateDocumentMetadataUserEditLoads()
      } finally {
        setIsSaving(false)
      }
    },
    [
      bqDocumentId,
      cascadeAlignedFieldsToPage,
      documentUserEdit,
      errorNotification,
      invalidateDocumentMetadataUserEditLoads,
      mutateDocumentUserEdit,
      saveDocumentMetadataUserEdit,
      systemDocument,
    ]
  )

  const saveDocumentMetadataFamily = useCallback(
    async (
      userValues: DocumentMetadataUserEditValues,
      members: readonly FamilyDocumentMetadataSaveMember[]
    ) => {
      setIsSaving(true)
      try {
        await saveDocumentMetadataUserEditFamily({
          members,
          userValues,
        })
        await saveCascadedPageMetadataFromDocumentFamily({
          sharedDocumentUserValues: userValues,
          members,
        })
        await mutateDocumentUserEdit()
        await mutatePageUserEdit()
        await invalidateDocumentMetadataUserEditLoads([
          ...new Set(members.map((member) => member.bqDocumentId)),
        ])
        await invalidateDocumentPageMetadataUserEditLoads()
      } finally {
        setIsSaving(false)
      }
    },
    [
      invalidateDocumentMetadataUserEditLoads,
      invalidateDocumentPageMetadataUserEditLoads,
      mutateDocumentUserEdit,
      mutatePageUserEdit,
      saveCascadedPageMetadataFromDocumentFamily,
      saveDocumentMetadataUserEditFamily,
    ]
  )

  return {
    isSaving,
    saveDocumentMetadata,
    saveDocumentMetadataFamily,
  }
}
